import type { ApiSchemas } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Button } from '@rental/ui'
import { ExternalLink, FileText } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAccess } from './access-context'
import { API_ORIGIN, APP_VERSION, apiFetch } from './api'
import { Dialog } from './components/Dialog'
import { ProblemNotice } from './components/ui'
import { createPress, fetchFile, fileOpener, type StaffFile } from './files'
import { useLocale } from './i18n'
import type { StaffProblem } from './problems'

/**
 * A1, the iPad app's side of files.ts: photos, identity documents and the
 * contract PDF fetched with the app's credentials and shown from memory. The
 * web app keeps its links (the callers choose by AUTH_MODE).
 */
const FROM_API = { origin: API_ORIGIN, appVersion: APP_VERSION, fetch: apiFetch }

/** A photo or document, larger, inside the app: the iPad app has no other window. */
export function ImageViewer({
  src,
  alt,
  onClose,
}: {
  src: string
  alt: string
  onClose: () => void
}) {
  const { messages: m } = useLocale()
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    // Once: React runs effects twice in development, and an open dialog stays open.
    const element = dialog.current
    if (element && !element.open) element.showModal()
  }, [])
  return (
    <Dialog dialogRef={dialog} label={alt} onClose={onClose} className="max-w-3xl p-4">
      <div className="flex flex-col gap-4">
        <img
          src={src}
          alt={alt}
          className="aspect-4/3 w-full rounded-sm bg-surface object-contain"
        />
        <div className="flex justify-end">
          <Button variant="outline" className="rounded-sm" onClick={() => dialog.current?.close()}>
            {m.files.close}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

type Thumb = { src: string | null; failed: boolean }

/**
 * A vehicle photo's bytes, fetched as it is shown and freed when it leaves the
 * screen. Photos only: an identity document is opened by a click, never here,
 * where a remount or a retry would log a view nobody made.
 */
function usePhoto(mediaId: string): Thumb {
  const { takeOver } = useAccess()
  const [thumb, setThumb] = useState<Thumb>({ src: null, failed: false })
  useEffect(() => {
    const controller = new AbortController()
    let url: string | null = null
    setThumb({ src: null, failed: false })
    void fetchFile({ kind: 'photo', mediaId }, { ...FROM_API, signal: controller.signal }).then(
      (result) => {
        if (controller.signal.aborted) return
        if (result.ok) {
          url = URL.createObjectURL(result.blob)
          setThumb({ src: url, failed: false })
          return
        }
        if (!takeOver(result.problem)) setThumb({ src: null, failed: true })
      },
    )
    return () => {
      controller.abort()
      if (url) URL.revokeObjectURL(url)
    }
  }, [mediaId, takeOver])
  return thumb
}

/**
 * Q-157: in the iPad app, a model photo taken in the app, as a picture and
 * nothing more (no viewer): fetched with the app's credentials as it is shown,
 * freed when it goes. The web app shows an image of the photo's link instead.
 */
export function MediaPhoto({
  mediaId,
  alt,
  className,
}: {
  mediaId: string
  alt: string
  className: string
}) {
  const { messages: m } = useLocale()
  const { src, failed } = usePhoto(mediaId)
  if (failed) {
    return (
      <p
        className={`flex items-center justify-center border border-dashed border-rule px-2 text-center text-sm text-text-muted ${className}`}
      >
        {m.files.photoUnavailable}
      </p>
    )
  }
  if (!src) {
    return (
      <div className={`border border-rule bg-bg ${className}`}>
        <span className="sr-only">{m.app.loading}</span>
      </div>
    )
  }
  return <img src={src} alt={alt} className={`border border-rule bg-bg ${className}`} />
}

/** One vehicle photo in a list: pressed, it opens larger in the viewer its list keeps. */
export function PhotoThumb({
  mediaId,
  alt,
  onOpen,
}: {
  mediaId: string
  alt: string
  onOpen: (src: string) => void
}) {
  const { messages: m } = useLocale()
  const { src, failed } = usePhoto(mediaId)
  if (failed) {
    return (
      <p className="flex aspect-4/3 items-center justify-center rounded-sm border border-dashed border-rule px-2 text-center text-sm text-text-muted">
        {m.files.photoUnavailable}
      </p>
    )
  }
  if (!src) {
    return (
      <div className="aspect-4/3 w-full rounded-sm border border-rule bg-bg">
        <span className="sr-only">{m.app.loading}</span>
      </div>
    )
  }
  return (
    <button type="button" className="block w-full" onClick={() => onOpen(src)}>
      <img
        src={src}
        alt={alt}
        className="aspect-4/3 w-full rounded-sm border border-rule bg-bg object-cover"
      />
    </button>
  )
}

/**
 * Opening a file the agent asked for: one press at a time (files.ts,
 * createPress), and in words when it went wrong. An image comes back to the
 * screen; a PDF goes to the opener.
 */
function usePress() {
  const { takeOver } = useAccess()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [press] = useState(() =>
    createPress({ fetch: (file) => fetchFile(file, FROM_API), opener: fileOpener, busy: setBusy }),
  )
  async function run(file: StaffFile, name: string): Promise<Blob | null> {
    setProblem(null)
    const pressed = await press(file, name)
    if (pressed?.kind === 'image') return pressed.blob
    if (pressed?.kind === 'problem' && !takeOver(pressed.problem)) setProblem(pressed.problem)
    return null
  }
  return { busy, problem, run }
}

/**
 * An identity document (CUS-2): fetched only when the agent presses Apri, so
 * every logged view is one the agent made. A photo opens in the viewer; a PDF
 * in the system's viewer, or, if the app's viewer did not start, the words say so.
 */
export function DocumentOpen({
  document,
  label,
}: {
  document: ApiSchemas['StaffDocument']
  label: string
}) {
  const { messages: m } = useLocale()
  const { busy, problem, run } = usePress()
  const [shown, setShown] = useState<string | null>(null)
  useEffect(() => () => (shown ? URL.revokeObjectURL(shown) : undefined), [shown])
  if (document.contentType === 'application/pdf' && !fileOpener()) {
    return <span className="text-sm text-text-muted">{m.documents.pdfNotInApp}</span>
  }

  async function open() {
    const file: StaffFile = { kind: 'document', mediaId: document.mediaId }
    const image = await run(file, `${document.kind}-${document.side}.pdf`)
    if (image) setShown(URL.createObjectURL(image))
  }

  return (
    <>
      <Button
        variant="link"
        busy={busy}
        aria-label={format(m.documents.openLabel, { document: label })}
        onClick={() => void open()}
      >
        <ExternalLink aria-hidden="true" className="size-4" />
        {m.documents.open}
      </Button>
      {problem ? (
        <div className="basis-full">
          <ProblemNotice problem={problem} />
        </div>
      ) : null}
      {shown ? <ImageViewer src={shown} alt={label} onClose={() => setShown(null)} /> : null}
    </>
  )
}

/** A PDF the API keeps, fetched when pressed; or why it cannot open here. */
function PdfPress({
  file,
  name,
  label,
  notInApp,
}: {
  file: StaffFile
  name: string
  label: string
  notInApp: string
}) {
  const { busy, problem, run } = usePress()
  if (!fileOpener()) {
    return <p className="text-sm text-text-muted">{notInApp}</p>
  }

  return (
    <div className="flex flex-col gap-2 self-start">
      <Button
        variant="link"
        busy={busy}
        className="gap-2 self-start"
        onClick={() => void run(file, name)}
      >
        <FileText aria-hidden="true" className="size-6" />
        {label}
      </Button>
      <ProblemNotice problem={problem} />
    </div>
  )
}

/** CTR-5: the signed contract. */
export function ContractPdf({ code }: { code: string }) {
  const { messages: m } = useLocale()
  return (
    <PdfPress
      file={{ kind: 'contract', code }}
      name={`contratto-${code}.pdf`}
      label={m.booking.contract.pdf}
      notInApp={m.booking.contract.pdfNotInApp}
    />
  )
}

/** S10: a printed fallback kit, from the iPad app. */
export function FallbackKitPdf({ kitId, kit }: { kitId: string; kit: string }) {
  const { messages: m } = useLocale()
  return (
    <PdfPress
      file={{ kind: 'fallbackKit', kitId }}
      name={`kit-di-emergenza-${kit}.pdf`}
      label={format(m.fallbackKit.open, { kit })}
      notInApp={m.fallbackKit.notInApp}
    />
  )
}

/** S6 (CMP-2): the letter naming a fine's renter; each press is one logged view. */
export function FineLetterPdf({ fineId, noticeNumber }: { fineId: string; noticeNumber: string }) {
  const { messages: m } = useLocale()
  return (
    <PdfPress
      file={{ kind: 'fineLetter', fineId }}
      name={`multa-${noticeNumber}.pdf`}
      label={m.fines.letter.open}
      notInApp={m.fines.letter.notInApp}
    />
  )
}
